<template>
  <q-layout view="lHh Lpr lFf">
    <q-page-container>
      <q-page class="auth-page flex flex-center">
        <div class="auth-wrap">

          <!-- Brand -->
          <div class="brand-section">
            <div class="brand-logo">
              <img src="../assets/icon2.png" alt="GoldenKey" />
            </div>
            <div class="brand-name">GoldenKey</div>
            <div class="brand-tagline">Portal Imobiliário</div>
          </div>

          <!-- Card -->
          <div class="auth-card">
            <div class="auth-card-header">
              <div class="auth-title">Criar Conta</div>
              <div class="auth-sub">Preencha os dados para se registar</div>
            </div>

            <!-- Success banner -->
            <div v-if="success" class="alert alert-success">
              <q-icon name="check_circle" size="18px" />
              <div>
                <div class="alert-title">Conta criada com sucesso!</div>
                <div class="alert-sub">Foi enviado um email para <strong>{{ emailActivate }}</strong> para concluir o registo.</div>
              </div>
            </div>

            <!-- Error banner -->
            <div v-if="submitError" class="alert alert-error">
              <q-icon name="error_outline" size="18px" />
              <div class="alert-title">{{ submitError }}</div>
            </div>

            <q-form @submit="onSubmit" @reset="onReset" class="auth-form">

              <!-- Nome -->
              <div class="field-wrap">
                <label class="field-label">Nome Completo</label>
                <q-input
                  v-model="formData.name"
                  outlined
                  dense
                  placeholder="O seu nome completo"
                  lazy-rules
                  :rules="nameRules"
                  class="auth-input"
                >
                  <template v-slot:prepend>
                    <q-icon name="person_outline" size="18px" />
                  </template>
                </q-input>
              </div>

              <!-- Email -->
              <div class="field-wrap">
                <label class="field-label">Email</label>
                <q-input
                  v-model="formData.email"
                  type="email"
                  outlined
                  dense
                  placeholder="o-seu@email.com"
                  lazy-rules
                  :rules="emailRules"
                  class="auth-input"
                >
                  <template v-slot:prepend>
                    <q-icon name="mail_outline" size="18px" />
                  </template>
                </q-input>
              </div>

              <!-- Senha -->
              <div class="field-wrap">
                <label class="field-label">Senha</label>
                <q-input
                  v-model="formData.password"
                  :type="showPassword ? 'text' : 'password'"
                  outlined
                  dense
                  placeholder="Mínimo 6 caracteres"
                  lazy-rules
                  :rules="passwordRules"
                  class="auth-input"
                >
                  <template v-slot:prepend>
                    <q-icon name="lock_outline" size="18px" />
                  </template>
                  <template v-slot:append>
                    <q-icon
                      :name="showPassword ? 'visibility_off' : 'visibility'"
                      size="18px"
                      class="cursor-pointer"
                      @click="showPassword = !showPassword"
                    />
                  </template>
                </q-input>
              </div>

              <!-- Confirmar Senha -->
              <div class="field-wrap">
                <label class="field-label">Confirmar Senha</label>
                <q-input
                  v-model="formData.password_confirmation"
                  :type="showPasswordConfirm ? 'text' : 'password'"
                  outlined
                  dense
                  placeholder="Repita a senha"
                  lazy-rules
                  :rules="passwordConfirmRules"
                  class="auth-input"
                >
                  <template v-slot:prepend>
                    <q-icon name="lock_outline" size="18px" />
                  </template>
                  <template v-slot:append>
                    <q-icon
                      :name="showPasswordConfirm ? 'visibility_off' : 'visibility'"
                      size="18px"
                      class="cursor-pointer"
                      @click="showPasswordConfirm = !showPasswordConfirm"
                    />
                  </template>
                </q-input>
              </div>

              <!-- Submit -->
              <button type="submit" class="submit-btn" :disabled="loading">
                <q-spinner-dots v-if="loading" color="white" size="20px" />
                <template v-else>
                  <span>Criar Conta</span>
                  <q-icon name="person_add" size="18px" />
                </template>
              </button>

            </q-form>

            <div class="auth-card-footer">
              Já tem uma conta?
              <button type="button" class="link-btn" @click="$router.push('/')">Fazer login</button>
            </div>
          </div>

          <!-- Footer -->
          <div class="page-footer">
            © {{ new Date().getFullYear() }} Plataforma Imobiliária · Um produto da
            <a href="https://marrar.co.mz" target="__blank">Marrar, LDA</a>
          </div>
        </div>
      </q-page>
    </q-page-container>
  </q-layout>
</template>

<script>
import { ref } from 'vue';
import store from '@/vuex/store';

export default {
  name: 'UserRegistration',

  setup() {

    const formData = ref({
      name: '',
      email: '',
      password: '',
      password_confirmation: ''
    });

    const showPassword = ref(false);
    const showPasswordConfirm = ref(false);
    const loading = ref(false);
    const success = ref(false);
    const submitError = ref('');
    const emailActivate = ref('');

    // Regras de validação
    const nameRules = [
      val => (val && val.length > 0) || 'Nome é obrigatório',
      val => (val && val.length >= 3) || 'Nome deve ter pelo menos 3 caracteres'
    ];

    const emailRules = [
      val => (val && val.length > 0) || 'Email é obrigatório',
      val => {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailPattern.test(val) || 'Email inválido';
      }
    ];

    const passwordRules = [
      val => (val && val.length > 0) || 'Senha é obrigatória',
      val => (val && val.length >= 6) || 'Senha deve ter pelo menos 6 caracteres'
    ];

    const passwordConfirmRules = [
      val => (val && val.length > 0) || 'Confirmação de senha é obrigatória',
      val => val === formData.value.password || 'As senhas não correspondem'
    ];

    const onSubmit = async () => {
      loading.value = true;
      success.value = false;
      submitError.value = '';

      try {

        await store.dispatch('auth/autoRegister', {
          name: formData.value.name,
          email: formData.value.email,
          password: formData.value.password,
          password_confirmation: formData.value.password_confirmation
        });

        // Sucesso
        emailActivate.value = formData.value.email;
        success.value = true;


        // Limpa o formulário
        formData.value = {
          name: '',
          email: '',
          password: '',
          password_confirmation: ''
        };

      } catch (error) {

        console.log(error)

        submitError.value = Object.values(error.response.data.errors)[0][0] || 'Erro ao registrar. Tente novamente.';


      } finally {
        loading.value = false;
      }
    };

    const onReset = () => {
      formData.value = {
        name: '',
        email: '',
        password: '',
        password_confirmation: ''
      };
      success.value = false;
      submitError.value = '';
    };

    return {
      formData,
      showPassword,
      showPasswordConfirm,
      emailActivate,
      loading,
      success,
      submitError,
      nameRules,
      emailRules,
      passwordRules,
      passwordConfirmRules,
      onSubmit,
      onReset
    };
  }
};
</script>

<style scoped>
/* ── Page ──────────────────────────────────────────── */
.auth-page {
  background: #0d1b2a;
  min-height: 100vh;
  padding: 32px 16px;
}

/* ── Wrap ──────────────────────────────────────────── */
.auth-wrap {
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* ── Brand ─────────────────────────────────────────── */
.brand-section {
  text-align: center;
  margin-bottom: 28px;
}
.brand-logo {
  width: 64px;
  height: 64px;
  margin: 0 auto 12px;
}
.brand-logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.brand-name {
  font-size: 22px;
  font-weight: 800;
  color: #d4af37;
  letter-spacing: 0.5px;
}
.brand-tagline {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.35);
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-top: 3px;
}

/* ── Card ──────────────────────────────────────────── */
.auth-card {
  width: 100%;
  background: #fff;
  border-radius: 20px;
  padding: 32px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.35);
}
.auth-card-header {
  margin-bottom: 22px;
}
.auth-title {
  font-size: 20px;
  font-weight: 800;
  color: #0d1b2a;
  margin-bottom: 4px;
}
.auth-sub {
  font-size: 13px;
  color: #94a3b8;
}

/* ── Alerts ────────────────────────────────────────── */
.alert {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 12px;
  margin-bottom: 18px;
  font-size: 13px;
}
.alert-success {
  background: rgba(22, 163, 74, 0.08);
  border: 1px solid rgba(22, 163, 74, 0.25);
  color: #15803d;
}
.alert-error {
  background: rgba(220, 38, 38, 0.07);
  border: 1px solid rgba(220, 38, 38, 0.2);
  color: #dc2626;
}
.alert-title { font-weight: 700; }
.alert-sub { font-size: 12px; margin-top: 2px; opacity: 0.85; }

/* ── Form ──────────────────────────────────────────── */
.auth-form {
  display: flex;
  flex-direction: column;
}
.field-wrap {
  margin-bottom: 14px;
}
.field-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 5px;
  letter-spacing: 0.2px;
}

/* Input overrides */
:deep(.auth-input .q-field__control) {
  border-radius: 12px;
}
:deep(.auth-input .q-field__control:before) {
  border-color: #e2e8f0;
}
:deep(.auth-input .q-field__control:hover:before) {
  border-color: #0d1b2a;
}
:deep(.auth-input.q-field--focused .q-field__control:before) {
  border-color: #d4af37 !important;
  border-width: 2px;
}
:deep(.auth-input .q-field__prepend),
:deep(.auth-input .q-field__append) {
  color: #b0bec5;
}
:deep(.auth-input.q-field--focused .q-field__prepend) {
  color: #0d1b2a;
}
:deep(.auth-input input::placeholder) {
  color: #c8d0d8;
}

/* Submit button */
.submit-btn {
  width: 100%;
  padding: 13px;
  border-radius: 12px;
  background: #0d1b2a;
  color: #d4af37;
  border: none;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 6px;
  transition: background 0.18s, transform 0.18s;
  letter-spacing: 0.3px;
}
.submit-btn:hover:not(:disabled) {
  background: #162333;
  transform: translateY(-1px);
}
.submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }

/* Card footer */
.auth-card-footer {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid #f1f5f9;
  text-align: center;
  font-size: 13px;
  color: #64748b;
}
.link-btn {
  background: none;
  border: none;
  font-size: 13px;
  font-weight: 700;
  color: #0d1b2a;
  cursor: pointer;
  padding: 0;
  margin-left: 4px;
  transition: color 0.15s;
}
.link-btn:hover { color: #d4af37; }

/* Page footer */
.page-footer {
  margin-top: 20px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.28);
  text-align: center;
  line-height: 1.7;
}
.page-footer a {
  color: rgba(212, 175, 55, 0.55);
  text-decoration: none;
  transition: color 0.15s;
}
.page-footer a:hover { color: #d4af37; }
</style>
